import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

/**
 * Phone helpers for entering a phone number as two manual parts:
 * a country calling code (e.g. "+44") and a national number (e.g. "7123456789").
 *
 * The country selector is intentionally NOT a dropdown — the user types the code —
 * but we still derive an example national number for whatever code they enter.
 */

export const DEFAULT_COUNTRY_CODE = '+44';

// calling code digits (e.g. "44") -> primary ISO country (first one wins)
let callingCodeToCountry: Map<string, CountryCode> | null = null;

function callingCodeMap(): Map<string, CountryCode> {
  if (callingCodeToCountry) {
    return callingCodeToCountry;
  }
  callingCodeToCountry = new Map<string, CountryCode>();
  for (const iso of getCountries()) {
    const code = getCountryCallingCode(iso);
    if (!callingCodeToCountry.has(code)) {
      callingCodeToCountry.set(code, iso);
    }
  }
  return callingCodeToCountry;
}

/** Normalises a typed country code to "+<digits>" (empty string if no digits). */
export function normalizeCountryCode(code: string | null | undefined): string {
  const digits = (code || '').replace(/\D/g, '');
  return digits ? '+' + digits : '';
}

/** Resolves the primary ISO country for a typed calling code like "+44" or "44". */
function countryForCode(code: string | null | undefined): CountryCode | undefined {
  const digits = (code || '').replace(/\D/g, '');
  return digits ? callingCodeMap().get(digits) : undefined;
}

/**
 * An example national number for the country the given calling code belongs to,
 * formatted the way it is written locally (e.g. "+44" -> "07400 123456").
 * Returns an empty string when the code is unknown.
 */
export function phoneExampleForCode(code: string | null | undefined): string {
  const iso = countryForCode(code);
  if (!iso) {
    return '';
  }
  try {
    const example = getExampleNumber(iso, examples);
    return example ? example.formatNational() : '';
  } catch {
    return '';
  }
}

/**
 * Combines a country code + national number into an E.164 string
 * (e.g. "+44", "7123456789" -> "+447123456789"), or undefined when the
 * national number is empty. Falls back to a plain concatenation when the
 * number cannot be parsed, so a valid-looking entry is never dropped.
 */
export function combineE164(countryCode: string | null | undefined, nationalNumber: string | null | undefined): string | undefined {
  const code = normalizeCountryCode(countryCode);
  const national = (nationalNumber || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!code || !national) {
    return undefined;
  }
  const candidate = code + national;
  try {
    const parsed = parsePhoneNumberFromString(candidate);
    if (parsed && parsed.isValid()) {
      return parsed.number;
    }
  } catch {
    // fall through to the raw candidate
  }
  return candidate;
}

/** Splits an E.164 string back into { countryCode, nationalNumber } for editing. */
export function splitE164(e164: string | null | undefined): { countryCode: string; nationalNumber: string } {
  const value = (e164 || '').trim();
  try {
    const parsed = parsePhoneNumberFromString(value);
    if (parsed) {
      return { countryCode: '+' + parsed.countryCallingCode, nationalNumber: parsed.nationalNumber as string };
    }
  } catch {
    // fall through to the naive split
  }
  const match = value.match(/^\+(\d{1,3})(\d+)$/);
  if (match) {
    return { countryCode: '+' + match[1], nationalNumber: match[2] };
  }
  return { countryCode: '', nationalNumber: value.replace(/^\+/, '') };
}
